import PropTypes from "prop-types"
import React, { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"

import Button from "@atoms/Button/Button"
import Heading from "@atoms/Heading/Heading"
import IconSvg from "@atoms/IconSvg/IconSvg"
import SearchInput from "@atoms/SearchInput/SearchInput"
import closeIcon from "@images/svg/close.svg"
import filtersIcon from "@images/svg/filters-icon.svg"
import RangeInput from "@molecules/RangeInput/RangeInput"
import MobileCheckboxFilter from "@organisms/MobileCheckboxFilter/MobileCheckboxFilter"

const MobileFilters = ({
  label,
  rangeLabel,
  filters,
  onCheck,
  onSave,
  onSearchChange,
  onRangeChange,
  showSearchAndPriceFields = true,
  showPriceFields = false,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [rangeValue, setRangeValue] = useState({ min: "", max: "" })
  const [openAccordionIndex, setOpenAccordionIndex] = useState(null)
  const checkboxRefs = useRef([])
  const { t } = useTranslation()

  const onFiltersToggle = () => {
    if (window.scrollY < 80) {
      //HEADER HEIGHT
      window.scroll({ top: 80, behavior: "smooth" })
    }

    setIsFilterOpen(!isFilterOpen)

    setTimeout(() => {
      //Set timeout is needed because safari pushes the scroll check on top after  this and nothing happens
      document.body.classList.toggle("overflow-hidden")
    }, 200)
  }

  const onInputChange = ({ target: { value, name } }) => {
    setRangeValue({ ...rangeValue, [name]: value })
  }

  useEffect(() => onRangeChange(rangeValue), [rangeValue])

  const resetSelections = () => {
    checkboxRefs.current.forEach((ref) => ref.resetState())
    setRangeValue({ min: "", max: "" })
  }

  return (
    <>
      <button
        className={`flex justify-between rounded-full w-full text-sm leading-3 font-medium text-blue-900 ${
          isFilterOpen ? "bg-gray-100" : "bg-gray-50"
        } py-2.5 px-3.5 ring-blue-900 ring-inset hover:ring-2`}
        onClick={onFiltersToggle}
      >
        {label}
        <IconSvg Icon={filtersIcon} widthClass="w-4.5" />
      </button>
      <div
        className={`fixed left-0 ${
          isFilterOpen ? "top-2" : "top-full"
        } transition-all bottom-0 z-30 w-full bg-white rounded-t-3xl overflow-auto`}
      >
        <div className="flex justify-between items-center p-3 border-b border-gray-50 border-solid">
          <Heading
            text="Rajaa hakutuloksia"
            level={6}
            customClasses="leading-1"
          />
          <button onClick={onFiltersToggle}>
            <IconSvg Icon={closeIcon} widthClass="3.5" />
          </button>
        </div>
        <div className="flex flex-col gap-5 items-start px-3 py-4">
          {showSearchAndPriceFields && (
            <>
              <SearchInput
                placeholder="Esim. kohteen nimi / osoite"
                widthClass="w-full"
                onChange={onSearchChange}
                onSearch={onSave}
              />
              {showPriceFields && (
                <div>
                  <Heading text={rangeLabel} level={6} customClasses="mb-2.5" />
                  <RangeInput
                    onInputChange={onInputChange}
                    value={rangeValue}
                  />
                </div>
              )}
            </>
          )}
          <ul className="w-full">
            {filters?.map((filterData, index) => {
              const isOpen = index === openAccordionIndex
              return (
                <MobileCheckboxFilter
                  key={filterData.heading}
                  index={index}
                  isOpen={isOpen}
                  openAccordionIndex={openAccordionIndex}
                  setOpenAccordionIndex={setOpenAccordionIndex}
                  onCheck={onCheck}
                  checkboxRefs={checkboxRefs}
                  {...filterData}
                />
              )
            })}
          </ul>
          <div className="w-full flex justify-between">
            <Button
              variant="whiteBlue"
              customClasses="w-full"
              action={resetSelections}
            >
              {t("Clear")}
            </Button>
            <Button
              variant="blueWhite"
              customClasses="w-full"
              action={() => {
                onSave()
                onFiltersToggle()
              }}
              margin="mr-0"
            >
              {t("Save")}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

MobileFilters.propTypes = {
  label: PropTypes.string.isRequired,
  rangeLabel: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      list: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        })
      ),
      initialState: PropTypes.objectOf(PropTypes.bool.isRequired),
      groupName: PropTypes.string.isRequired,
      textSearchSettings: PropTypes.shape({
        apply: PropTypes.bool,
        placeholder: PropTypes.string,
      }),
    })
  ).isRequired,
  onCheck: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onRangeChange: PropTypes.func.isRequired,
}

export default MobileFilters
